import { BaseControl } from ".."
import {
    IconButtonGroupControl,
    FlexRowIcon, FlexColumnIcon,
    JustifyStartIcon, JustifyCenterIcon, JustifyEndIcon,
    JustifySpaceBetweenIcon, JustifySpaceAroundIcon,
    AlignStartIcon, AlignCenterIcon, AlignEndIcon, AlignStretchIcon,
} from "../IconButtonGroupControl"
import { NumberCssControl } from "../NumberCssControl";

/** @typedef {import('./../../components/CssUnitSelect').CssUnit} CssUnit */

/**
 * @typedef {object} FlexControlOptions
 */

/**
 * @typedef {object} FlexControlValue
 * @property {string} flexDirection - Flex direction (e.g. 'row', 'column')
 * @property {string} justifyContent - Justify content (e.g. 'flex-start', 'center', 'flex-end', 'space-between', 'space-around')
 * @property {string} alignItems - Align items (e.g. 'flex-start', 'center', 'flex-end', 'stretch')
 * @property {{ value: number, unit: CssUnit }} gap - Gap between flex items (e.g. { value: 10, unit: 'px' })
 * @property {{ value: number, unit: CssUnit }} [basis] - Basis for flex items (e.g. { value: 100, unit: 'px' })
 */

/**
 * @extends {BaseControl<FlexControlValue, FlexControlOptions>}
 */
export class FlexControls extends BaseControl {

    /**
     * @param {import('..').BaseControlDefinition<FlexControlValue, FlexControlOptions>} control 
     */
    constructor(control) {
        super(control);

        this.children = {
            'flexDirection': new IconButtonGroupControl({
                label: 'Richting',
                defaultValue: 'column',
                options: {
                    buttons: [
                        { 
                            value: 'row', 
                            icon: <FlexRowIcon />, 
                            tooltip: 'Horizontaal' 
                        },
                        { 
                            value: 'column', 
                            icon: <FlexColumnIcon />, 
                            tooltip: 'Verticaal' 
                        },
                        // { 
                        //     value: 'row-reverse', 
                        //     icon: <FlexRowReverseIcon />, 
                        //     tooltip: 'Row Reverse' 
                        // },
                        // { 
                        //     value: 'column-reverse', 
                        //     icon: <FlexColumnReverseIcon />, 
                        //     tooltip: 'Column Reverse' 
                        // },
                    ]
                }
            }),
            'justifyContent': new IconButtonGroupControl({
                label: 'Verdeling',
                defaultValue: 'flex-start',
                options: {
                    buttons: [
                        { 
                            value: 'flex-start', 
                            icon: <JustifyStartIcon />, 
                            tooltip: 'Begin' 
                        },
                        { 
                            value: 'center', 
                            icon: <JustifyCenterIcon />, 
                            tooltip: 'Midden' 
                        },
                        { 
                            value: 'flex-end', 
                            icon: <JustifyEndIcon />, 
                            tooltip: 'Einde' 
                        },
                        { 
                            value: 'space-between', 
                            icon: <JustifySpaceBetweenIcon />, 
                            tooltip: 'Gelijk verdeeld (ruimte tussen)' 
                        },
                        { 
                            value: 'space-around', 
                            icon: <JustifySpaceAroundIcon />, 
                            tooltip: 'Gelijk verdeeld (ruimte rondom)' 
                        },
                    ]
                }
            }),
            'alignItems': new IconButtonGroupControl({
                label: 'Uitlijning',
                defaultValue: 'stretch',
                options: {
                    buttons: [
                        { 
                            value: 'flex-start', 
                            icon: <AlignStartIcon />, 
                            tooltip: 'Begin' 
                        },
                        { 
                            value: 'center', 
                            icon: <AlignCenterIcon />, 
                            tooltip: 'Midden' 
                        },
                        { 
                            value: 'flex-end', 
                            icon: <AlignEndIcon />, 
                            tooltip: 'Einde' 
                        },
                        { 
                            value: 'stretch', 
                            icon: <AlignStretchIcon />, 
                            tooltip: 'Uitrekken' 
                        },
                    ]
                }
            }),
            'gap': new NumberCssControl({
                label: 'Ruimte',
                description: 'Ruimte tussen items',
                defaultValue: { value: 10, unit: 'px' },
                options: {
                    min: 0,
                    max: 100,
                    step: 1,
                }
            }),
            // 'basis': new NumberCssControl({
            //     label: 'Breedte',
            //     description: 'Breedte van dit item',
            //     defaultValue: { value: 100, unit: '%' },
            //     options: {
            //         min: 0,
            //         max: 1000,
            //         step: 1,
            //     }
            // }),
        }
    }

    /**
     * Overrides the base render to swap justify/align icons when the
     * flex direction is 'column', so icons correctly reflect the
     * vertical main axis and horizontal cross axis.
     * @param {FlexControlValue} value
     * @param {(value: FlexControlValue) => void} onChange
     */
    render(value, onChange) {
        const isColumn = (value?.flexDirection ?? 'column') === 'column';

        /* Justify icons: row = horizontal axis, column = vertical axis.
           Rotating row icons -90° gives correct column representations. */
        this.children['justifyContent'].options.buttons = [
            {
                value: 'flex-start',
                icon: isColumn
                    ? <Rotate angle={90}><JustifyStartIcon /></Rotate>
                    : <JustifyStartIcon />,
                tooltip: 'Begin',
            },
            {
                value: 'center',
                icon: isColumn
                    ? <Rotate><JustifyCenterIcon /></Rotate>
                    : <JustifyCenterIcon />,
                tooltip: 'Midden',
            },
            {
                value: 'flex-end',
                icon: isColumn
                    ? <Rotate angle={90}><JustifyEndIcon /></Rotate>
                    : <JustifyEndIcon />,
                tooltip: 'Einde',
            },
            {
                value: 'space-between',
                icon: isColumn
                    ? <Rotate><JustifySpaceBetweenIcon /></Rotate>
                    : <JustifySpaceBetweenIcon />,
                tooltip: 'Gelijk verdeeld (ruimte tussen)',
            },
            {
                value: 'space-around',
                icon: isColumn
                    ? <Rotate><JustifySpaceAroundIcon /></Rotate>
                    : <JustifySpaceAroundIcon />,
                tooltip: 'Gelijk verdeeld (ruimte rondom)',
            },
        ];

        /* Align icons: row = vertical cross axis, column = horizontal
           cross axis. Rotating row icons -90° gives column variants. */
        this.children['alignItems'].options.buttons = [
            {
                value: 'flex-start',
                icon: isColumn
                    ? <Rotate angle={-90}><AlignStartIcon /></Rotate>
                    : <AlignStartIcon />,
                tooltip: 'Begin',
            },
            {
                value: 'center',
                icon: isColumn
                    ? <Rotate><AlignCenterIcon /></Rotate>
                    : <AlignCenterIcon />,
                tooltip: 'Midden',
            },
            {
                value: 'flex-end',
                icon: isColumn
                    ? <Rotate angle={-90}><AlignEndIcon /></Rotate>
                    : <AlignEndIcon />,
                tooltip: 'Einde',
            },
            {
                value: 'stretch',
                icon: isColumn
                    ? <Rotate><AlignStretchIcon /></Rotate>
                    : <AlignStretchIcon />,
                tooltip: 'Uitrekken',
            },
        ];

        return super.render(value, onChange);
    }
}

/**
 * Wraps an icon component and rotates it -90° to convert row-axis icons into column-axis icons.
 * @param {object} props
 * @param {number} [props.angle=90] - The angle to rotate the icon (default is 90 degrees)
 * @param {React.ReactNode} props.children - The icon to rotate
 */
const Rotate = ({ angle = 90, children }) => (
    <span style={{ display: 'inline-flex', transform: `rotate(${angle}deg)` }}>
        {children}
    </span>
);
